import { odata } from '@bingads-webui/http-util';
import _ from 'underscore';
import { Promise } from 'bluebird';
import { AzureUploader, FilePayload } from '@bingads-webui/azure-upload';
import { constants } from './constants';

export class VideoOData {
  constructor(
    cid,
    aid,
    oDataConfig,
    activityLogger
  ) {
    this.cid = cid;
    this.aid = aid;
    this.baseURL = `${oDataConfig.odataPath}/Customers(${this.cid})/Accounts(${this.aid})`;
    this.oDataConfig = {
      oDataToken: oDataConfig.odataToken,
      oDataTokenType: oDataConfig.oDataTokenType,
      applicationName: oDataConfig.applicationName,
    };
    this.uploader = new AzureUploader({
      logger: activityLogger ? {
        trace: activityLogger.signal,
        error: activityLogger.error,
      } : {
        trace: _.noop,
        error: _.noop,
      },
    });
  }

  getUploadUrl({ fileExtension }) {
    const url = `${this.baseURL}/Videos/Default.GetUploadUrl(fileExtension='${fileExtension}')`;
    return odata.get(url, this.oDataConfig);
  }

  getUploadUrlForRepair({ fileExtension, id }) {
    const url = `${this.baseURL}/Videos/${id}/Default.GetUploadUrlForRepair(fileExtension='${fileExtension}')`;
    return odata.get(url, this.oDataConfig);
  }

  postVideoByFile({
    file, description, onProgress,
  }) {
    return new Promise((resolve, reject) => {
      const { name } = file;
      const nameSplits = name.split('.');
      const ext = nameSplits[nameSplits.length - 1];
      this.getUploadUrl({ fileExtension: `.${ext}` })
        .then((blob) => {
          const { BlobName } = blob;
          const token = this.uploader.uploadPayloadToBlobWithCancellation({
            blob,
            payload: new FilePayload({ file }),
            onProgress,
          });
          token.chain(() => odata.post(`${this.baseURL}/Videos/`, {
            ...this.oDataConfig,
            urlParameters: { blobName: BlobName, description },
          }));
          resolve(token);
        })
        .catch(reject);
    });
  }

  postVideoByUrl({ videoUrl }) {
    return odata.post(`${this.baseURL}/Videos/`, {
      ...this.oDataConfig,
      urlParameters: { videoUrl, description: videoUrl },
    });
  }

  getVideos({
    sortById,
    searchTerm,
    pageNumber,
    pageSize,
  }) {
    const top = pageSize;
    const skip = pageNumber * pageSize;

    const sortDefinition = _.find(
      constants.videoSortBySelections,
      sortBySelection => sortBySelection.id === sortById
    );
    const sortQuery = sortDefinition.query;

    const query = {
      $top: top,
      $skip: skip,
      count: true,
    };
    /* istanbul ignore if */
    if (searchTerm.length > 0) {
      query.$filter = `contains(Text, '${searchTerm}')`;
    }

    return odata.get(
      `${this.baseURL}/Videos/?${sortQuery}`,
      {
        ...this.oDataConfig,
        data: query,
        customHeader: { name: 'x-ms-refreshcampaigncache', value: 'true' },
      }
    );
  }

  replaceVideoByUrl({ videoUrl, id }) {
    return odata.post(`${this.baseURL}/Videos/${id}/RepairVideo`, {
      ...this.oDataConfig,
      data: {
        videoUrl, description: videoUrl,
      },
    });
  }

  replaceVideoByBlob({ id, file, onProgress }) {
    return new Promise((resolve, reject) => {
      const { name } = file;
      const nameSplits = name.split('.');
      const ext = nameSplits[nameSplits.length - 1];
      this.getUploadUrlForRepair({ fileExtension: `.${ext}`, id })
        .then((blob) => {
          const { BlobName } = blob;
          const token = this.uploader.uploadPayloadToBlobWithCancellation({
            blob,
            payload: new FilePayload({ file }),
            onProgress,
          });
          token.chain(() => odata.post(`${this.baseURL}/Videos/${id}/RepairVideo`, {
            ...this.oDataConfig,
            data: {
              blobName: BlobName, description: name,
            },
          }));
          resolve(token);
        })
        .catch(reject);
    });
  }

  getVideo({ id }) {
    return odata.get(`${this.baseURL}/Videos/${id}`, this.oDataConfig);
  }

  patchVideo({ id, deltaVideo }) {
    return odata.patch(`${this.baseURL}/Videos/${id}`, { ...this.oDataConfig, data: deltaVideo });
  }

  deleteVideo({ id }) {
    return odata.delete(`${this.baseURL}/Videos/${id}`, this.oDataConfig);
  }

  /* istanbul ignore next */
  createVideoBasedOnOriginalVideo({ id, data }) {
    return odata.post(`${this.baseURL}/Videos/${id}/Default.CreateVideoBasedOnOriginalVideo`, {
      ...this.oDataConfig,
      data,
    });
  }
}
